/**
 * App Component
 * Main application component with routing
 */

import { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Layout } from './components/layout/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ErrorFallback } from './components/ErrorFallback';
import { apiClient } from './services/api';
import { serviceProviderService } from './services/service-provider.service';

// Pages
import { Home } from './pages/Home';
import { Dashboard } from './pages/Dashboard';
import { MyProjects } from './pages/MyProjects';
import { Account } from './pages/Account';
import { AuthVerify } from './pages/AuthVerify';
import { PlanCreate } from './pages/PlanCreate';
import { PlanDetail } from './pages/PlanDetail';
import { PlanEdit } from './pages/PlanEdit';
import { ServiceProviders } from './pages/ServiceProviders';
import { JobDetail } from './pages/JobDetail';
import { ServiceProviderLogin } from './pages/ServiceProviderLogin';
import { ServiceProviderSignup } from './pages/ServiceProviderSignup';
import { SubscriptionPlans } from './pages/SubscriptionPlans';
import { SubscriptionDashboard } from './pages/SubscriptionDashboard';
import { SubscriptionCheckoutSuccess } from './pages/SubscriptionCheckoutSuccess';
import { StripeConnectCallback } from './pages/StripeConnectCallback';
import { InvoiceGenerator } from './pages/InvoiceGenerator';
import { InvoicePaymentPage } from './pages/InvoicePaymentPage';

// Legal Pages
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Disclaimer } from './pages/Disclaimer';
import { Accessibility } from './pages/Accessibility';

// Documentation Pages
import { DocsHub } from './pages/docs/DocsHub';
import { CalGreenOverview } from './pages/docs/CalGreenOverview';
import { MaterialsGuide } from './pages/docs/MaterialsGuide';
import { FAQ } from './pages/docs/FAQ';
import { Examples } from './pages/docs/Examples';
import { Glossary } from './pages/docs/Glossary';

// County Pages
import { CountyPage } from './pages/counties/[slug]';

// Credentialing Pages
import { RegistrationPage } from './features/credentialing/routes/RegistrationPage';
import { RequestInvitePage } from './features/credentialing/routes/RequestInvitePage';

// Marketplace Pages
import { JobBrowser } from './pages/marketplace/JobBrowser';
import { JobDetails } from './pages/marketplace/JobDetails';
import { MyBids } from './pages/marketplace/MyBids';

// Service Provider Dashboard Pages (New DDD-Compliant Structure)
import { ProviderDashboard } from './pages/service-provider/ProviderDashboard';
import { JobsPage } from './pages/service-provider/JobsPage';
import { BidsPage } from './pages/service-provider/BidsPage';
import { DispatchPage } from './pages/service-provider/DispatchPage';
import { TicketsPage } from './pages/service-provider/TicketsPage';
import { MessagesPage } from './pages/service-provider/MessagesPage';
import { ResourcesPage } from './pages/service-provider/ResourcesPage';
import { WidgetConfigPage } from './pages/service-provider/WidgetConfigPage';
import { SettingsPage } from './pages/service-provider/SettingsPage';

// Resource Management Components
import { ContainersTab } from './components/resources/containers/ContainersTab';
import { StorageYardsTab } from './components/resources/storage-yards/StorageYardsTab';
import { VehiclesTab } from './components/resources/vehicles/VehiclesTab';
import { DriversTab } from './components/resources/drivers/DriversTab';

// Widget Pages (White-Label Customer Portal)
import { WidgetLayout } from './components/widget/WidgetLayout';
import { WidgetRegister } from './pages/widget/WidgetRegister';
import { WidgetLogin } from './pages/widget/WidgetLogin';
import { WidgetDashboard } from './pages/widget/WidgetDashboard';
import { ProtectedWidgetRoute } from './components/widget/ProtectedWidgetRoute';
import { ServiceCatalog } from './pages/widget/ServiceCatalog';
import { OrderFlow } from './pages/widget/OrderFlow';
import { Checkout } from './pages/widget/Checkout';
import { OrderConfirmation } from './pages/widget/OrderConfirmation';
import { OrderList } from './pages/widget/OrderList';
import { OrderDetail } from './pages/widget/OrderDetail';

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  // Configure API client to handle 401 errors
  // Only redirect if not already on auth pages (let components handle errors there)
  useEffect(() => {
    apiClient.setUnauthorizedHandler(() => {
      const isProviderRoute = location.pathname.startsWith('/provider/');
      const isAuthPage = location.pathname.startsWith('/provider/login') ||
                        location.pathname.startsWith('/provider/signup');

      if (isProviderRoute && !isAuthPage) {
        // Clear service provider auth tokens
        serviceProviderService.logout();
        // Use React Router navigation (async, preserves React state)
        navigate('/provider/login', { replace: true });
      }
    });
  }, [navigate, location]);

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Routes>
        <Route path="/" element={<Layout />}>
        {/* ============================================
            PUBLIC ROUTES
            General access, no authentication required
            ============================================ */}
        <Route index element={<Home />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="my-projects" element={<MyProjects />} />
        <Route path="account" element={<Account />} />
        <Route path="auth/verify" element={<AuthVerify />} />

        {/* Waste Management Plans */}
        <Route path="plans/create" element={<PlanCreate />} />
        <Route path="plans/:id/create" element={<PlanCreate />} />
        <Route path="plans/:id" element={<PlanDetail />} />
        <Route path="plans/:id/edit" element={<PlanEdit />} />

        {/* Jobs */}
        <Route path="jobs/:id" element={<JobDetail />} />
        <Route path="jobs/:jobId/invoices/new" element={<InvoiceGenerator />} />

        {/* Invoices */}
        <Route path="invoices/:invoiceId/pay" element={<InvoicePaymentPage />} />

        {/* Public Directory */}
        <Route path="service-providers" element={<ServiceProviders />} />

        {/* Legal Pages */}
        <Route path="privacy" element={<Privacy />} />
        <Route path="terms" element={<Terms />} />
        <Route path="disclaimer" element={<Disclaimer />} />
        <Route path="accessibility" element={<Accessibility />} />

        {/* Documentation Pages */}
        <Route path="docs" element={<DocsHub />} />
        <Route path="docs/calgreen-overview" element={<CalGreenOverview />} />
        <Route path="docs/materials-guide" element={<MaterialsGuide />} />
        <Route path="docs/faq" element={<FAQ />} />
        <Route path="docs/examples" element={<Examples />} />
        <Route path="docs/glossary" element={<Glossary />} />

        {/* County Pages */}
        <Route path="counties/:slug" element={<CountyPage />} />

        {/* ============================================
            PROVIDER ROUTES
            Service provider workspace
            ============================================ */}

        {/* Authentication */}
        <Route path="provider/login" element={<ServiceProviderLogin />} />
        <Route path="provider/signup" element={<ServiceProviderSignup />} />

        {/* Registration (Invite-based) */}
        <Route path="provider/register" element={<RegistrationPage />} />
        <Route path="provider/request-invite" element={<RequestInvitePage />} />

        {/* DDD-Compliant Dashboard with Nested Routes */}
        <Route path="provider/dashboard" element={<ProviderDashboard />}>
          <Route path="jobs" element={<JobsPage />} />
          <Route path="bids" element={<BidsPage />} />
          <Route path="dispatch" element={<DispatchPage />} />
          <Route path="tickets" element={<TicketsPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="resources" element={<ResourcesPage />}>
            <Route path="containers" element={<ContainersTab />} />
            <Route path="storage-yards" element={<StorageYardsTab />} />
            <Route path="vehicles" element={<VehiclesTab />} />
            <Route path="drivers" element={<DriversTab />} />
          </Route>
          <Route path="widget" element={<WidgetConfigPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Subscription Management */}
        <Route path="provider/subscription/plans" element={<SubscriptionPlans />} />
        <Route path="provider/subscription/dashboard" element={<SubscriptionDashboard />} />
        <Route path="provider/subscription/success" element={<SubscriptionCheckoutSuccess />} />

        {/* Stripe Integration */}
        <Route path="provider/stripe/callback" element={<StripeConnectCallback />} />

        {/* Marketplace */}
        <Route path="provider/marketplace" element={<JobBrowser />} />
        <Route path="provider/marketplace/jobs/:id" element={<JobDetails />} />
        <Route path="provider/marketplace/my-bids" element={<MyBids />} />

        {/* ============================================
            WIDGET ROUTES
            White-label customer portal
            ============================================ */}
        {/* Widget routes are wrapped in ProviderConfigProvider for theming */}
        {/* 404 Not Found */}
        <Route
          path="*"
          element={
            <div className="card text-center py-12">
              <h1 className="text-4xl font-bold text-neutral-900 mb-2">404</h1>
              <p className="text-neutral-600 mb-6">Page not found</p>
              <a href="/" className="btn-primary">
                Go Home
              </a>
            </div>
          }
        />
      </Route>

      {/* Widget Routes (standalone, no Layout wrapper) */}
      <Route path="/widget/:providerSlug" element={<WidgetLayout />}>
        {/* Public widget routes - New e-commerce flow */}
        <Route index element={<ServiceCatalog />} />
        <Route path="order" element={<OrderFlow />} />
        <Route path="checkout" element={<Checkout />} />
        <Route path="confirmation/:orderId" element={<OrderConfirmation />} />

        {/* Authentication routes */}
        <Route path="register" element={<WidgetRegister />} />
        <Route path="login" element={<WidgetLogin />} />

        {/* Protected widget routes */}
        <Route
          path="dashboard"
          element={
            <ProtectedWidgetRoute>
              <WidgetDashboard />
            </ProtectedWidgetRoute>
          }
        />
        <Route
          path="orders"
          element={
            <ProtectedWidgetRoute>
              <OrderList />
            </ProtectedWidgetRoute>
          }
        />
        <Route
          path="orders/:orderId"
          element={
            <ProtectedWidgetRoute>
              <OrderDetail />
            </ProtectedWidgetRoute>
          }
        />
      </Route>
    </Routes>
    <Toaster position="top-right" richColors />
    </ErrorBoundary>
  );
}

export default App;